/**
 * Editor modal de post-its — Fase 10a.2.
 *
 * Reutilizable para CREAR y EDITAR. La diferencia es solo qué valores
 * inicializa y qué texto pinta en los botones. El caller decide qué
 * endpoint llamar con el payload resuelto.
 *
 * Modelo:
 *   const result = await openPostitEditor({
 *     mode: 'create',
 *     pilots: [...],
 *     currentUserId: 'uuid',
 *     defaultPosition: { xPercent: 50, yPercent: 50 },
 *   });
 *   if (result) await api.createNotamPostit({ ...result });
 *
 * Reglas del dominio aplicadas en la UI:
 *  - El campo `targetPilotId='self'` no tiene sentido — no se permite
 *    elegirse a uno mismo como destinatario (mensaje al escuadrón
 *    es la opción correcta).
 *  - Si `targetPilotId` es null, `visibility` se fuerza a public y los
 *    radios privacy quedan deshabilitados.
 *  - Body máx 500 chars (contador en vivo).
 */

import type { NotamPostit, PilotSummary, Visibility } from '@51esc/shared';

import './editor.css';

export type EditorMode = 'create' | 'edit';

export interface PostitEditorOptions {
  mode: EditorMode;
  pilots: PilotSummary[];
  currentUserId: string;
  /** Valores iniciales (existente al editar; posición default al crear). */
  initial?: Partial<
    Pick<NotamPostit, 'body' | 'targetPilotId' | 'visibility' | 'xPercent' | 'yPercent'>
  >;
}

export interface PostitEditorResult {
  body: string;
  targetPilotId: string | null;
  visibility: Visibility;
  xPercent: number;
  yPercent: number;
}

const MAX_BODY = 500;

interface ModalRefs {
  dialog: HTMLDialogElement;
  title: HTMLElement;
  bodyTa: HTMLTextAreaElement;
  bodyCount: HTMLElement;
  targetSel: HTMLSelectElement;
  visGroup: HTMLElement;
  visPublic: HTMLInputElement;
  visPrivate: HTMLInputElement;
  saveBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  errorBox: HTMLElement;
}

let refs: ModalRefs | null = null;

export function openPostitEditor(
  opts: PostitEditorOptions,
): Promise<PostitEditorResult | null> {
  const r = ensureModal();

  r.title.textContent =
    opts.mode === 'create' ? 'NUEVO POST-IT' : 'EDITAR POST-IT';
  r.saveBtn.textContent = opts.mode === 'create' ? 'Crear' : 'Guardar';

  const initialBody = opts.initial?.body ?? '';
  r.bodyTa.value = initialBody;
  updateCount(r, initialBody);

  populateTargetSelect(r.targetSel, opts.pilots, opts.currentUserId);
  const initialTarget = opts.initial?.targetPilotId ?? null;
  r.targetSel.value = initialTarget ?? '';

  const initialVis: Visibility = opts.initial?.visibility ?? 'public';
  r.visPublic.checked = initialVis === 'public';
  r.visPrivate.checked = initialVis === 'private';
  syncVisibilityEnabled(r);

  const initialX = opts.initial?.xPercent ?? 50;
  const initialY = opts.initial?.yPercent ?? 50;

  r.errorBox.hidden = true;
  r.errorBox.textContent = '';

  return new Promise<PostitEditorResult | null>((resolve) => {
    let resolved = false;
    const finish = (value: PostitEditorResult | null): void => {
      if (resolved) return;
      resolved = true;
      r.dialog.close();
      r.saveBtn.removeEventListener('click', onSave);
      r.cancelBtn.removeEventListener('click', onCancel);
      r.dialog.removeEventListener('cancel', onCancel);
      r.dialog.removeEventListener('click', onBackdrop);
      r.bodyTa.removeEventListener('input', onBodyInput);
      r.targetSel.removeEventListener('change', onTargetChange);
      resolve(value);
    };
    const onCancel = (ev?: Event): void => {
      ev?.preventDefault();
      finish(null);
    };
    const onBackdrop = (ev: MouseEvent): void => {
      if (ev.target === r.dialog) finish(null);
    };
    const onBodyInput = (): void => updateCount(r, r.bodyTa.value);
    const onTargetChange = (): void => syncVisibilityEnabled(r);
    const onSave = (): void => {
      const body = r.bodyTa.value.trim();
      if (body.length === 0) {
        showError(r, 'El texto es obligatorio.');
        return;
      }
      if (body.length > MAX_BODY) {
        showError(r, `Máximo ${MAX_BODY} caracteres.`);
        return;
      }
      const targetPilotId = r.targetSel.value === '' ? null : r.targetSel.value;
      let visibility: Visibility = r.visPrivate.checked ? 'private' : 'public';
      if (targetPilotId === null) visibility = 'public';
      finish({
        body,
        targetPilotId,
        visibility,
        xPercent: initialX,
        yPercent: initialY,
      });
    };

    r.saveBtn.addEventListener('click', onSave);
    r.cancelBtn.addEventListener('click', onCancel);
    r.dialog.addEventListener('cancel', onCancel);
    r.dialog.addEventListener('click', onBackdrop);
    r.bodyTa.addEventListener('input', onBodyInput);
    r.targetSel.addEventListener('change', onTargetChange);

    r.dialog.showModal();
    r.bodyTa.focus();
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────

function ensureModal(): ModalRefs {
  if (refs !== null) return refs;
  const dialog = document.createElement('dialog');
  dialog.className = 'pe-dialog';
  dialog.innerHTML = `
    <form class="pe-card" method="dialog" novalidate>
      <h2 class="pe-title"></h2>

      <label class="pe-field">
        <span class="pe-label">Texto</span>
        <textarea class="pe-textarea" rows="5" maxlength="500" required></textarea>
        <small class="pe-count" aria-live="polite">0 / ${MAX_BODY}</small>
      </label>

      <label class="pe-field">
        <span class="pe-label">Destinatario</span>
        <select class="pe-select">
          <option value="">— Al escuadrón —</option>
        </select>
      </label>

      <fieldset class="pe-field pe-visibility">
        <legend class="pe-label">Visibilidad</legend>
        <label class="pe-radio">
          <input type="radio" name="pe-vis" value="public" />
          Público
        </label>
        <label class="pe-radio">
          <input type="radio" name="pe-vis" value="private" />
          Privado al destinatario
        </label>
      </fieldset>

      <p class="pe-error" role="alert" hidden></p>

      <div class="pe-actions">
        <button type="button" class="pe-btn pe-cancel">Cancelar</button>
        <button type="button" class="pe-btn pe-save"></button>
      </div>
    </form>
  `;
  document.body.appendChild(dialog);

  const get = <T extends HTMLElement>(sel: string): T => {
    const el = dialog.querySelector<T>(sel);
    if (!el) throw new Error(`[postit-editor] missing ${sel}`);
    return el;
  };
  refs = {
    dialog,
    title: get<HTMLElement>('.pe-title'),
    bodyTa: get<HTMLTextAreaElement>('.pe-textarea'),
    bodyCount: get<HTMLElement>('.pe-count'),
    targetSel: get<HTMLSelectElement>('.pe-select'),
    visGroup: get<HTMLElement>('.pe-visibility'),
    visPublic: get<HTMLInputElement>('input[name="pe-vis"][value="public"]'),
    visPrivate: get<HTMLInputElement>('input[name="pe-vis"][value="private"]'),
    saveBtn: get<HTMLButtonElement>('.pe-save'),
    cancelBtn: get<HTMLButtonElement>('.pe-cancel'),
    errorBox: get<HTMLElement>('.pe-error'),
  };
  return refs;
}

function populateTargetSelect(
  sel: HTMLSelectElement,
  pilots: PilotSummary[],
  currentUserId: string,
): void {
  // Limpiar opciones excepto la primera (Al escuadrón).
  while (sel.options.length > 1) sel.remove(1);
  const ordered = pilots
    .filter((p) => p.id !== currentUserId)
    .slice()
    .sort((a, b) => a.callsign.localeCompare(b.callsign));
  for (const p of ordered) {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.textContent = p.callsign;
    sel.append(opt);
  }
}

function syncVisibilityEnabled(r: ModalRefs): void {
  const hasTarget = r.targetSel.value !== '';
  r.visPrivate.disabled = !hasTarget;
  if (!hasTarget) {
    r.visPublic.checked = true;
    r.visPrivate.checked = false;
  }
  r.visGroup.classList.toggle('pe-visibility--disabled', !hasTarget);
}

function updateCount(r: ModalRefs, value: string): void {
  r.bodyCount.textContent = `${value.length} / ${MAX_BODY}`;
}

function showError(r: ModalRefs, msg: string): void {
  r.errorBox.textContent = msg;
  r.errorBox.hidden = false;
}
